import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'

function Home() {
  const [hotLures, setHotLures] = useState([])
  const [recentReviews, setRecentReviews] = useState([])
  const [siteStats, setSiteStats] = useState({ lures: null, reviews: null, members: null })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchHomeData()
  }, [])

  const fetchHomeData = async () => {
    try {
      const [
        { count: lureCount },
        { count: reviewCount },
        { count: memberCount },
        { data: luresData },
        { data: reviewsData },
      ] = await Promise.all([
        supabase.from('lure_variants').select('*', { count: 'exact', head: true }),
        supabase.from('lure_reviews').select('*', { count: 'exact', head: true }),
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase
          .from('base_lures')
          .select(`
            base_lure_id,
            name,
            brands (brand_name),
            lure_types (type_name),
            lure_variants (variant_id)
          `)
          .limit(10),
        supabase
          .from('lure_reviews')
          .select(`
            review_id,
            rating,
            title,
            created_at,
            base_lures (base_lure_id, name, brands (brand_name))
          `)
          .order('created_at', { ascending: false })
          .limit(5),
      ])

      setSiteStats({ lures: lureCount, reviews: reviewCount, members: memberCount })

      const luresWithCounts = luresData?.map(lure => ({
        ...lure,
        variantCount: lure.lure_variants?.length || 0
      })) || []

      setHotLures(luresWithCounts)
      setRecentReviews(reviewsData || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching home data:', error)
      setLoading(false)
    }
  }

  const formatCount = (n) => {
    if (n === null || n === undefined) return '—'
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
    return n.toLocaleString()
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <div className="hero-section">
        <h2>Welcome to FishinWiseGuys!</h2>
        <p className="hero-text">
          A community where fishing enthusiasts can easily track, rate, review and discuss their prized collection of fishing lures.
          Users can also manage trade lists - what they want and what they have to offer!
        </p>
        <div className="hero-stats">
          <div className="stat">
            <strong>{formatCount(siteStats.lures)}</strong>
            <span>Lures in Database</span>
          </div>
          <div className="stat">
            <strong>{formatCount(siteStats.reviews)}</strong>
            <span>User Reviews</span>
          </div>
          <div className="stat">
            <strong>{formatCount(siteStats.members)}</strong>
            <span>Active Members</span>
          </div>
        </div>
      </div>

      <div className="home-grid">
        {/* Hot Lures Section */}
        <section className="home-section">
          <h3 className="section-title">🔥 Hot Lures</h3>
          <div className="lure-list">
            {hotLures.map((lure, index) => (
              <div key={lure.base_lure_id} className="lure-item">
                <span className="rank">#{index + 1}</span>
                <div className="lure-info">
                  <Link to={`/lure/${lure.base_lure_id}`} className="lure-name">
                    {lure.name}
                  </Link>
                  <span className="lure-meta">
                    {lure.brands?.brand_name} | {lure.lure_types?.type_name}
                    {lure.variantCount > 0 && ` | ${lure.variantCount} colors`}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Link to="/browse" className="view-all-link">View All Lures →</Link>
        </section>

        {/* Recent Reviews Section */}
        <section className="home-section">
          <h3 className="section-title">📝 Recent Reviews</h3>
          <div className="review-list">
            {recentReviews.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>No reviews yet — be the first!</p>
            ) : (
              recentReviews.map((review) => (
                <div key={review.review_id} className="review-item">
                  <div className="review-rating">
                    {review.rating}/10
                  </div>
                  <div className="review-info">
                    <Link to={`/lure/${review.base_lures?.base_lure_id}`} className="review-lure">
                      {review.base_lures?.name}
                    </Link>
                    <p className="review-title">{review.title || 'No title'}</p>
                    <span className="review-date">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <Link to="/browse" className="view-all-link">View All Reviews →</Link>
        </section>
      </div>

      {/* Feature Highlights */}
      <section className="features-section">
        <h3>What You Can Do on FishinWiseGuys</h3>
        <div className="features-grid">
          <div className="feature">
            <h4>📊 Rate & Review</h4>
            <p>Share your experience with fishing gear and help others make informed decisions</p>
          </div>
          <div className="feature">
            <h4>🎣 Track Collection</h4>
            <p>Manage your tackle box inventory and create wishlists</p>
          </div>
          <div className="feature">
            <h4>💬 Discuss</h4>
            <p>Join gear-specific forums and connect with fellow anglers</p>
          </div>
          <div className="feature">
            <h4>📹 Watch Videos</h4>
            <p>See gear in action with integrated YouTube reviews</p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
